import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { FaPaperclip, FaTimes } from 'react-icons/fa';
import AuthContext from '../../../context/AuthContext';
import { createTicket, TICKET_CATEGORIES } from '../../../services/supportTicketsApi';
import { useSingleFlight } from '../../../hooks/useSingleFlight';
import styles from '../HelpPage.module.css';

const MAX_FILES = 5;

const RaiseTicket = () => {
    const authCtx = useContext(AuthContext);
    const [category, setCategory] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [files, setFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [feedback, setFeedback] = useState({ type: '', text: '' });

    const onFileChange = (e) => {
        const picked = Array.from(e.target.files || []);
        const combined = [...files, ...picked].slice(0, MAX_FILES);
        setFiles(combined);
        e.target.value = '';
    };

    const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

    const resetForm = () => {
        setCategory(''); setSubject(''); setMessage(''); setFiles([]);
    };

    const onSubmit = useSingleFlight(async (e) => {
        e.preventDefault();
        setFeedback({ type: '', text: '' });
        if (!category) { setFeedback({ type: 'error', text: 'Please choose a category.' }); return; }
        if (!subject.trim()) { setFeedback({ type: 'error', text: 'Subject is required.' }); return; }
        if (!message.trim()) { setFeedback({ type: 'error', text: 'Message is required.' }); return; }
        if (!authCtx.token) { setFeedback({ type: 'error', text: 'You must be logged in to raise a ticket.' }); return; }

        setSubmitting(true);
        try {
            await createTicket(authCtx.token, { category, subject: subject.trim(), message: message.trim(), attachments: files });
            setFeedback({ type: 'success', text: 'Ticket submitted! You can track it under Manage Support Tickets.' });
            resetForm();
        } catch (err) {
            setFeedback({ type: 'error', text: err.message || 'Failed to submit ticket.' });
        } finally {
            setSubmitting(false);
        }
    });

    return (
        <section className={styles.helpSection}>
            <h2>Raise a Ticket</h2>
            <p>Have an issue or feature request? Tell us about it and our team will get back to you.</p>

            <form onSubmit={onSubmit} style={{ maxWidth: 640, marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontWeight: 500, color: '#505050', fontSize: '0.9rem' }}>Category <span style={{ color: 'red' }}>*</span></span>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #D0E7FF', outline: 'none', fontSize: '0.95rem' }}
                    >
                        <option value="">Select a category…</option>
                        {TICKET_CATEGORIES.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                    </select>
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontWeight: 500, color: '#505050', fontSize: '0.9rem' }}>Subject <span style={{ color: 'red' }}>*</span></span>
                    <input
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        maxLength={200}
                        placeholder="Short summary of the issue"
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #D0E7FF', outline: 'none', fontSize: '0.95rem' }}
                    />
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontWeight: 500, color: '#505050', fontSize: '0.9rem' }}>Message <span style={{ color: 'red' }}>*</span></span>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={6}
                        placeholder="Describe what happened, what you expected, and any steps to reproduce."
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #D0E7FF', outline: 'none', fontSize: '0.95rem', resize: 'vertical' }}
                    />
                </label>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontWeight: 500, color: '#505050', fontSize: '0.9rem' }}>Attachments <span style={{ color: '#737373', fontWeight: 400 }}>(optional, up to {MAX_FILES})</span></span>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px dashed #78B3F3', cursor: 'pointer', color: '#2766AA', maxWidth: 220, fontSize: '0.9rem' }}>
                        <FaPaperclip /> Attach files
                        <input
                            type="file"
                            multiple
                            onChange={onFileChange}
                            style={{ display: 'none' }}
                        />
                    </label>
                    {files.length > 0 && (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {files.map((f, i) => (
                                <li key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F8FBFF', padding: '6px 10px', borderRadius: 6, fontSize: '0.85rem', color: '#505050' }}>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                                    <button type="button" onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#737373' }} aria-label="Remove">
                                        <FaTimes />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {feedback.text && (
                    <div style={{ fontSize: '0.9rem', color: feedback.type === 'error' ? '#d9534f' : '#2E9E5C' }}>
                        {feedback.text}
                        {feedback.type === 'success' && (
                            <>
                                {' '}
                                <Link to="/dashboard/support-tickets" style={{ color: '#2766AA', fontWeight: 500 }}>View my tickets</Link>
                            </>
                        )}
                    </div>
                )}

                <div>
                    <button
                        type="submit"
                        disabled={submitting}
                        style={{ padding: '10px 22px', background: '#2766AA', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1 }}
                    >
                        {submitting ? 'Submitting…' : 'Submit Ticket'}
                    </button>
                </div>
            </form>
        </section>
    );
};

export default RaiseTicket;
